import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';

const fetchUsers = async (page: number, pageSize: number) => {
  const response = await axiosInstance.get(`/users`, {
    params: {
      page: page + 1,
      limit: pageSize,
    },
  });

  return {
    data: Array.isArray(response.data) ? response.data : response.data.data || [],
    total: response.data.total || (Array.isArray(response.data) ? response.data.length : 0),
  };
};

export const UserList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['users', paginationModel.page, paginationModel.pageSize],
    queryFn: () => fetchUsers(paginationModel.page, paginationModel.pageSize),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => axiosInstance.delete(`/users/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      showMessage('User deleted successfully', 'success');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to delete user', 'error');
    },
  });

  const handleDeleteClick = (id: string) => {
    setUserToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (userToDelete) {
      deleteMutation.mutate(userToDelete);
    }
  };

  const getRoleColor = (roleSystemKey: string) => {
    switch (roleSystemKey) {
      case 'super_admin':
        return 'error';
      case 'org_owner':
      case 'store_owner':
        return 'primary';
      case 'store_manager':
      case 'warehouse_manager':
        return 'secondary';
      case 'store_staff':
        return 'default';
      case 'customer':
        return 'info';
      default:
        return 'default';
    }
  };

  // Get user's roles from various sources (multi-role system)
  const getUserRoles = (row: any): { id: string; name: string; systemKey: string }[] => {
    // New multi-role: check roles array
    if (row.roles?.length) {
      return row.roles;
    }
    // Multi-role via userRoles junction table
    if (row.userRoles?.length) {
      return row.userRoles.map((ur: any) => ur.role).filter(Boolean);
    }
    // Fallback to roleSystemKeys if available
    if (row.roleSystemKeys?.length) {
      return row.roleSystemKeys.map((key: string) => ({
        id: key,
        name: key.replace(/_/g, ' '),
        systemKey: key,
      }));
    }
    return [];
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
      valueGetter: (params: any) => `${params.row.firstName} ${params.row.lastName}`,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 150,
    },
    {
      field: 'roles',
      headerName: 'Roles',
      width: 200,
      renderCell: (params) => {
        const userRolesList = getUserRoles(params.row);
        if (!userRolesList.length) {
          return (
            <Typography variant="caption" color="text.secondary">
              No roles
            </Typography>
          );
        }
        const maxDisplay = 2;
        const displayedRoles = userRolesList.slice(0, maxDisplay);
        const remainingCount = userRolesList.length - maxDisplay;

        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
            {displayedRoles.map((role) => (
              <Chip
                key={role.id}
                label={role.name?.replace(/_/g, ' ') || role.systemKey?.replace(/_/g, ' ')}
                color={getRoleColor(role.systemKey) as any}
                size="small"
              />
            ))}
            {remainingCount > 0 && (
              <Tooltip
                title={userRolesList
                  .slice(maxDisplay)
                  .map((r) => r.name?.replace(/_/g, ' ') || r.systemKey)
                  .join(', ')}
              >
                <Chip label={`+${remainingCount}`} size="small" variant="outlined" />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'organizationName',
      headerName: 'Organization',
      width: 180,
      valueGetter: (params: any) => params.row.organization?.name || 'N/A',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.isActive ? 'Active' : 'Inactive'}
          color={params.row.isActive ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => navigate(`/users/edit/${params.row.id}`)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDeleteClick(params.row.id)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Users</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/users/create')}
        >
          Create User
        </Button>
      </Box>

      <Card>
        <CardContent>
          <DataGrid
            rows={data?.data || []}
            columns={columns}
            rowCount={data?.total || 0}
            loading={isLoading}
            pageSizeOptions={[5, 10, 25, 50]}
            paginationModel={paginationModel}
            paginationMode="server"
            onPaginationModelChange={setPaginationModel}
            autoHeight
            disableRowSelectionOnClick
            disableVirtualization={process.env.NODE_ENV === 'test'}
          />
        </CardContent>
      </Card>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this user? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
